import { applyTransform2Object } from '@bit/gerenciatd.utils.common';
import FrontSonda from '@bit/gerenciatd.utils.front-sonda';
import { getUUID } from '@bit/gerenciatd.utils.monitoring';
import {
  MethodType,
  UnknownFunctionWithUnknownReturn,
} from '@bit/gerenciatd.utils.types';
import Request, { getMockedInfo } from '@bit/gerenciatd.utils.request';
import Sonda from '@bit/gerenciatd.utils.sonda';

export const processName = 'ADABYRON-API';
export const processNameTges = `${processName}-TGES`;
export const processNameOpen = `${processName}-OPEN`;

export enum USER_TYPES {
  CLIENT = 'Telco',
  TGES = 'Gestor',
  OPEN = '',
}

type Params = string | number | boolean;
export type QueryParams = Array<{
  param: string;
  value?: Params | undefined | null;
}>;
export type URLParamsToReplace = Array<{
  param: string;
  value: Params;
}>;

export const buildApiUrl = (
  endpoint: string,
  idRequest: string,
  queryParams?: QueryParams,
  urlParamsToReplace?: URLParamsToReplace,
): string => {
  let URL_API = `${endpoint}?${idRequest}`;

  if (queryParams) {
    queryParams.forEach((queryParam) => {
      const { param, value } = queryParam;

      if (param && value) {
        URL_API += `&${param}=${value}`;
      } else if (
        param &&
        !Object.prototype.hasOwnProperty.call(queryParam, 'value')
      ) {
        URL_API += `&${param}`;
      }
    });
  }

  if (urlParamsToReplace) {
    urlParamsToReplace.forEach((paramToReplace) => {
      const { param, value } = paramToReplace;
      URL_API = URL_API.replace(param, String(value));
    });
  }

  return URL_API;
};

export interface GeneralAdabyronBehaviourParams {
  context: any;
  idFront: string;
  path: string;
  userTypes: USER_TYPES[];
  eventName: string;
  queryParams?: QueryParams;
  urlParamsToReplace?: URLParamsToReplace;
  mockedInfo: string;
  methodType: MethodType;
  requestObject?: Record<string, ObjectToTransformValue>;
  onErrorCallback?: UnknownFunctionWithUnknownReturn;
  onSuccessCallback?: UnknownFunctionWithUnknownReturn;
  onSuccessNormalizer?: (datos: unknown) => unknown;
  requestHeaders?: Record<string, string>;
}

/**
 * generalAdabyronBehaviour: Comportamiento predefinido para una promesa de Adabyron que se puede personalizar.
 * @param context Contexto necesario
 * @param idFront IDFront necesario
 * @param path  Path necesario
 * @param {array} userTypes Tipo de usuario que puede acceder al método
 * @param {string} eventName Nombre del evento
 * @param {array} queryParams (optional): Parámetros a añadir a la URL
 * @param {array} urlParamsToReplace (optional): Parámetros a reemplazar en la URL
 * @param {string} mockedInfo Mocked info
 * @param {MethodType} methodType Tipo de método en la request: GET, POST, PUT, HEAD
 * @param {object} requestObject (optional): Datos necesarios para la request => (PUT / POST)
 * @param {function} onErrorCallback (optional): Callback que se ejecuta cuando la petición ha sido rechazada.
 * @param {function} onSuccessCallback (optional): Callback que se ejecuta cuando la petición ha ido correctamente.
 * @param {function} onSuccessNormalizer (optional): Función normalizadora para los datos cuando la petición ha ido correctamente.
 * @param {object} requestHeaders (optional): Headers extra necesarios
 * @returns {promise} Promesa de la request
 */

export const generalAdabyronBehaviour = (
  params: GeneralAdabyronBehaviourParams,
): Promise<unknown> => {
  const {
    context,
    idFront,
    path,
    userTypes,
    eventName,
    queryParams,
    urlParamsToReplace,
    mockedInfo,
    methodType,
    requestObject,
    onErrorCallback,
    onSuccessCallback,
    onSuccessNormalizer,
    requestHeaders,
  } = params;

  const idRequest = getUUID(idFront);
  const { name } = context.environment;

  const { endpoint, headers, processName, allowed } = getTelcoHost(
    context,
    path,
    userTypes,
  );

  if (!allowed) {
    return getMethodNotAllowedReject(path);
  }

  const sonda = new Sonda({
    eventName,
    context,
    uniqueId: idRequest,
    origin: processName,
  });

  const URL_API = buildApiUrl(
    endpoint,
    idRequest,
    queryParams,
    urlParamsToReplace,
  );

  const mockInfo = getMockedInfo(mockedInfo, name);

  const onSuccessDefault = (xhr: XMLHttpRequest) => {
    const { response, responseURL } = xhr;
    if (isRedirectToLoginError(responseURL)) {
      makeLoginRedirect(responseURL);
    } else {
      const { datos } = JSON.parse(response);
      return onSuccessNormalizer ? onSuccessNormalizer(datos) : datos;
    }
  };

  try {
    const request = new Request({
      method: methodType,
      data:
        (methodType === MethodType.POST || methodType === MethodType.PUT) &&
        requestObject &&
        requestObject,
      endpoint: URL_API,
      onError:
        onErrorCallback ||
        ((response: XMLHttpRequest) => normalizeObjError(response, context)),
      onSuccess:
        onSuccessCallback ||
        ((response: XMLHttpRequest) => onSuccessDefault(response)),
      sonda,
      headers: requestHeaders ? { ...headers, ...requestHeaders } : headers,
      mockInfo,
    });

    return request.makeRequest();
  } catch (err) {
    sonda.errorJs(err);
    return null;
  }
};

export const isRedirectToLoginError = (responseUrl: string): boolean => {
  if (responseUrl && responseUrl.indexOf('DesafioUnico') !== -1) {
    return true;
  }
  return false;
};

export const normalizeRedirectError = (responseUrl: string): string => {
  const urlActual = window.location.href;

  if (window.location.hostname === 'www.movistar.es') {
    // eslint-disable-next-line max-len
    return `https://www.movistar.es/mimovistar-cliente/publico/servicio/login?urlContenedora=${responseUrl}&urlDestino=${encodeURIComponent(
      urlActual,
    )}`;
  }
  if (window.location.hostname === 'www.movistar.es.cert') {
    // eslint-disable-next-line max-len
    return `https://www.movistar.es.cert/mimovistar-cliente/publico/servicio/login?urlContenedora=${responseUrl}&urlDestino=${encodeURIComponent(
      urlActual,
    )}`;
  }
  if (window.location.hostname === 'www.movistar.es.inte') {
    // eslint-disable-next-line max-len
    return `https://www.movistar.es.inte/mimovistar-cliente/publico/servicio/login?urlContenedora=${responseUrl}&urlDestino=${encodeURIComponent(
      urlActual,
    )}`;
  }
  return '#';
};

export const makeLoginRedirect = (responseUrl: string): void => {
  window.location.href = normalizeRedirectError(responseUrl);
};

type ObjectToTransformValue =
  | string
  | number
  | boolean
  | Record<string, unknown>
  | Array<unknown>
  | null;

export const makeUndefinedValueNull = (
  obj: Record<string, ObjectToTransformValue>,
): Record<string, ObjectToTransformValue> => {
  return applyTransform2Object(
    [
      {
        type: 'undefined',
        method: () => null,
      },
    ],
    (key) => key,
    obj,
  ) as Record<string, ObjectToTransformValue>;
};

export const getMethodNotAllowedReject = (path: string): Promise<any> => {
  return Promise.reject({
    status: 418,
    statusText: "I'm a teapot",
    response: `Method not allowed for this type of user: ${path}`,
  });
};

export const getTelcoHost = (
  context: any,
  path: string,
  userTypesAllowed: string[],
  version?: number,
): {
  endpoint: string;
  headers: any;
  processName: string;
  allowed: boolean;
} => {
  try {
    const { environment, userType } = context;

    if (userTypesAllowed.includes(userType)) {
      let { telco: connectionData } = environment;
      let prefixToApply = '/cliente/api/';
      let finalProcessName = processName;

      if (userType === USER_TYPES.TGES) {
        ({ tges: connectionData } = environment);
        prefixToApply = '';
        finalProcessName = processNameTges;
      }
      if (userType === USER_TYPES.OPEN) {
        prefixToApply = '/abierto/api/';
        finalProcessName = processNameOpen;
      }

      const { endpoint: host, headers } = connectionData;
      const pathBegin = path.substr(0, path.indexOf('/'));
      const pathEnd = path.substr(path.indexOf('/') + 1, path.length);
      let finalEndpoint = `${host}${prefixToApply}${pathBegin}/`;
      if (version) {
        finalEndpoint = `${finalEndpoint}v${version}/`;
      }

      return {
        endpoint: `${finalEndpoint}${pathEnd}`,
        headers,
        processName: finalProcessName,
        allowed: true,
      };
    } else {
      return {
        endpoint: '',
        headers: {},
        processName: '',
        allowed: false,
      };
    }
  } catch (error) {
    console.error(error);
    const frontSonda = new FrontSonda({
      processName: 'getTelcoHost',
      context,
      filePath: 'utils/ada-byron',
    });
    frontSonda.errorMessage({ info: `error on path ${path}`, error });
  }
  return null;
};

export const normalizeObjError = (
  xhr: XMLHttpRequest,
  context: any,
): Partial<XMLHttpRequest> => {
  const objError = {
    status: xhr.status,
    statusText: xhr.statusText,
    response: null,
  };

  if (objError.status === 401 || objError.status === 400) {
    const { userType } = context;
    if (userType === USER_TYPES.TGES) {
      window.location.href = `${context.environment.tges.endpoint}autorizacion/acceso`;
    }
  }
  try {
    objError.response = JSON.parse(xhr.response);
  } catch (error) {
    objError.response = xhr.response;
  }

  return objError;
};
